import { useEffect, useMemo, useState } from 'react';
import has from 'lodash/has';
import { useHistory, useLocation } from 'react-router';
import { generateQueryParametersFromLocation } from 'src/utils';

/*
    Internal filters implies they don't make api calls.
    Simply filter the data in the rendered ui.
    We need have to create any additional state to show filtered data.

    Expected Params - (dataArray, initialFilterConfig, routeModifyingFiltersMap)

    initialFilterConfig:
    // should be an object
    {
        [id]: {
            value: '',  // Initial Value of the filter used during setup
            filterPredicate: () => {} // Defines logic for how do you want to filter the data
        }
    }


    filters & activeFilter will be an internal state in this hook.
    filterState would be an object like 
    {
        id: ""          // unique identifier for the filter
        value: any      // value for the filter,
    }

    filterPredicate: () => {
            The default implmentation
            Simply looks for the [id] key in your data and match against the value to filter.
            
            
            filter state can have a custom implementation of this. 
            But it needs to be explicitly provided in the intialFilterConfig params.
    }

    returs filters, dataAfterFiltering
    data must not be a state . 

    routeModifyingFiltersMap is a map of filterKey strings that we want to modify the url for.
    {[key: filterKey]: {
      routeKey: 'correspondingRouteKey',
      defaultValue: ''  // default value of the filter in case its not present in the locationState 
    }}
*/

const setupInitialState = (initialFilterConfig = {}) =>
  Object.entries(initialFilterConfig).reduce((filters, [filterKey, filterConfig]) => {
    if (filterConfig.value) filters[filterKey] = filterConfig.value;
    return filters;
  }, {});

const applyFilters = (data, filters, initialFilterConfig = {}) => {
  let entries = Object.entries(filters);
  if (entries.length > 0) {
    return data.filter(dataRow => {
      // Using a for loop to avoid extra iteration
      for (let i = 0; i < entries.length; i++) {
        if (initialFilterConfig[entries[i][0]]?.filterPredicate) {
          if (!initialFilterConfig[entries[i][0]].filterPredicate(dataRow, entries[i][1]))
            return false;
          continue;
        } else {
          // default logic
          if (entries[i][1] === '') continue;
          if (has(dataRow, entries[i][0]) && dataRow[entries[i][0]] !== entries[i][1]) return false;
        }
      }
      return true;
    });
  }
  return data;
};

const useInternalFilters = (data, initialFilterConfig = {}, routeModifyingFiltersMap = {}) => {
  const history = useHistory();
  const location = useLocation();
  const [filters, updateFilters] = useState(setupInitialState(initialFilterConfig));
  const filteredData = useMemo(() => applyFilters(data, filters, initialFilterConfig), [
    filters,
    data,
    initialFilterConfig,
  ]);

  const onFiltersChange = ({ key, value }) => {
    if (key) {
      if (routeModifyingFiltersMap[key]) {
        history.push(
          {
            pathname: location.pathname,
            search: generateQueryParametersFromLocation(location, {
              [routeModifyingFiltersMap[key].routeKey]: value,
            }),
          },
          {
            ...location.state,
            [routeModifyingFiltersMap[key].routeKey]: value,
          }
        );
      } else {
        updateFilters(prev => ({ ...prev, [key]: value }));
      }
    }
  };

  useEffect(() => {
    const routeModifyingFilterKeys = Object.keys(routeModifyingFiltersMap);
    if (routeModifyingFilterKeys.length) {
      const newFiltersState = routeModifyingFilterKeys.reduce((routeFilterMap, filterKey) => {
        routeFilterMap[filterKey] =
          location.state?.[routeModifyingFiltersMap[filterKey].routeKey] ||
          initialFilterConfig[routeModifyingFiltersMap[filterKey].routeKey]?.value ||
          routeModifyingFiltersMap[filterKey].defaultValue;
        return routeFilterMap;
      }, {});
      updateFilters(prev => ({ ...prev, ...newFiltersState }));
    }
  }, [location.state]);

  return {
    filters,
    data: filteredData,
    updateFilters: onFiltersChange,
  };
};

export { useInternalFilters };
